import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';

import { ActivityIndicator } from '../../atoms';

export type PullToRefreshWrapperProps = {
	children: React.ReactNode;
	onPullToRefresh: () => Promise<unknown>;
	refreshTimeout?: number;
};

const throttle = 150;
const SPINNER_TOP_POSITION = 45;
const PULL_TO_REFRESH_THRESHOLD = 150;
const MAX = 130;
const k = 0.6;
const translateWithTension = (x: number) => {
	return MAX * (1 - Math.exp((-k * x) / MAX));
};

export const PullToRefreshWrapper = ({
	children,
	onPullToRefresh,
	refreshTimeout
}: PullToRefreshWrapperProps): JSX.Element => {
	const [showSpinner, setShowSpinner] = useState(false);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const dy = useRef(-1);
	const initialY = useRef(0);
	const wrapperRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const el = wrapperRef.current;

		if (!el) return;

		el.addEventListener('touchstart', handlePullStart, { passive: true });

		return () => {
			el.removeEventListener('touchstart', handlePullStart);
		};
	}, [wrapperRef.current]);

	const handlePullStart = (startEvent: TouchEvent) => {
		if (!wrapperRef.current) return;
		initialY.current = startEvent?.touches[0]?.clientY ?? 0;

		wrapperRef.current.addEventListener(
			'touchmove',
			(moveEvent: TouchEvent) => {
				handlePullMoveTranslateAnimation(moveEvent);
				debouncedPull();
			},
			{ passive: true }
		);
	};

	const handlePullEnd = () => {
		if (!wrapperRef.current) return;

		wrapperRef.current.style.transform = 'translateY(0)';
		wrapperRef.current.style.transition = 'transform 0.3s';
		setShowSpinner(false);

		dy.current = 0;
	};

	const handlePullMoveTranslateAnimation = (moveEvent: TouchEvent) => {
		if (!wrapperRef.current) return;

		const currentY = moveEvent?.touches[0]?.clientY ?? 0;

		dy.current = currentY - initialY.current;

		const translateY = window.scrollY > 0 ? 0 : translateWithTension(dy.current);

		wrapperRef.current.style.transform = `translateY(${translateY}px)`;
	};

	const handlePullCallback = () => {
		if (!wrapperRef.current) return;
		setShowSpinner(true);

		if (dy.current < 0 || dy.current <= PULL_TO_REFRESH_THRESHOLD || window.scrollY > 0 || isRefreshing) {
			handlePullEnd();

			return;
		}
		if (!isRefreshing) {
			onPullToRefresh().then(() => handlePullEnd());
		}

		if (refreshTimeout) {
			setIsRefreshing(true);
			setTimeout(() => setIsRefreshing(false), refreshTimeout);
		}
	};

	const debouncedPull = useDebouncedCallback(handlePullCallback, throttle);

	return (
		<S.Container>
			{showSpinner && (
				<S.LoaderWrapper>
					<ActivityIndicator />
				</S.LoaderWrapper>
			)}
			<div ref={wrapperRef}>{children}</div>
		</S.Container>
	);
};

namespace S {
	export const Container = styled.div`
		z-index: 1;
		position: relative;
		width: 100%;
	`;

	export const LoaderWrapper = styled.div`
		position: absolute;
		left: 50%;
		transform: translate(-50%, -50%);
		transition: all 0.3s ease-in-out;
		top: ${SPINNER_TOP_POSITION}px;
		z-index: 0;
	`;
}
